/**
 * Neutralizes the unhandled rejections of view transitions.
 *
 * Nuxt's internal plugin does `transition.finished.then(resetTransitionState)`
 * without `.catch()` (`nuxt/dist/app/plugins/view-transitions.client.js`). When the
 * browser aborts the transition, `finished` rejects, the promise derived by
 * that `.then()` rejects in turn with nobody to handle it, and the error
 * surfaces as an unhandled rejection all the way to PostHog and Sentry.
 *
 * What it produced in production on `dashboard.printags.com`: 22
 * occurrences for 7 people, which had become the first source of client
 * exceptions, for two perfectly benign causes.
 *
 *   InvalidStateError: Transition was aborted because of invalid state
 *     -> the tab went to the background during the navigation
 *   TimeoutError: Transition was aborted because of timeout in DOM update
 *     -> the DOM update exceeded the 4 s imposed by the specification
 *
 * In both cases the navigation completes, only the animation is skipped. The
 * noise, however, drowned out the real errors.
 *
 * Rather than filtering the symptom in both reporters, we address the
 * cause: the promises exposed by `ViewTransition` are replaced by
 * versions that already have a rejection handler. Nuxt still receives
 * its `then` (the state is properly reset, aborted or not) and nothing
 * ends up as an unhandled rejection anymore.
 *
 * To remove once Nuxt's plugin has its `.catch()` upstream.
 */
export default defineNuxtPlugin({
  name: "view-transition-noise",
  // Before the application plugins: the method must be replaced before the
  // first navigation, given that Nuxt reads `document.startViewTransition`
  // at call time and not at its initialization.
  enforce: "pre",
  setup() {
    const doc = document as Document & {
      startViewTransition?: (callback: () => unknown) => ViewTransitionLike;
      __printagsViewTransitionPatched?: boolean;
    };

    if (typeof doc.startViewTransition !== "function") return;
    if (doc.__printagsViewTransitionPatched) return;

    const native = doc.startViewTransition.bind(doc);

    // Attaching a rejection handler is enough to mark the promise as
    // handled. We return the derived promise, which resolves instead of rejecting:
    // that is what prevents Nuxt's `.then()` from producing an orphan rejection
    // in turn.
    const handled = (promise: unknown) =>
      promise instanceof Promise ? promise.catch(() => undefined) : promise;

    doc.startViewTransition = (callback: () => unknown) => {
      const transition = native(callback);
      try {
        // Own properties defined on the instance: they shadow the
        // prototype accessors without replacing the object, so
        // `skipTransition()` keeps its native receiver and stays callable.
        Object.defineProperties(transition, {
          ready: { value: handled(transition.ready), configurable: true },
          updateCallbackDone: { value: handled(transition.updateCallbackDone), configurable: true },
          finished: { value: handled(transition.finished), configurable: true }
        });
      } catch {
        // If the browser refuses the redefinition, we fall back to simply
        // marking the original promises: Nuxt's `.then()` will stay
        // noisy, but nothing is broken.
        handled(transition.ready);
        handled(transition.updateCallbackDone);
        handled(transition.finished);
      }
      return transition;
    };

    doc.__printagsViewTransitionPatched = true;
  }
});

interface ViewTransitionLike {
  ready: Promise<void>;
  updateCallbackDone: Promise<void>;
  finished: Promise<void>;
  skipTransition: () => void;
}
